/**
 * Карточка доставки: с чем ехать, кому звонить и что дальше.
 *
 * Кнопки перехода показываются только те, что разрешает автомат статусов
 * (`TRANSITION_ACTORS`). Это удобство, а не защита: решение всё равно
 * принимает сервер, и скрытая кнопка ничего не запрещает.
 */
import { useState } from 'react';
import { ERROR_CODES, type CurrentUser, type Order, type OrderStatus } from '@arbuz/shared';
import { ApiError, api } from '../api';
import { formatMoney, makeT, orderStatusLabel } from '../i18n';
import { haptic } from '../tg';
import { dangerButton, rowStyle, secondaryButton, statusColor } from './courierUi';

interface Props {
  user: CurrentUser;
  order: Order;
  /**
   * Может ли исполнитель сам подтвердить заказ. Зависит от настройки
   * площадки: при `order_confirmation_mode = 'admin'` подтверждает
   * администратор. Решает всё равно сервер — здесь только кнопка.
   */
  canConfirm: boolean;
  onChanged: () => void;
  onDeliver: () => void;
  onFail: () => void;
  onBack: () => void;
}

export function CourierOrderCard({
  user,
  order,
  canConfirm,
  onChanged,
  onDeliver,
  onFail,
  onBack,
}: Props) {
  const t = makeT(user.locale);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function move(to: OrderStatus) {
    haptic('medium');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/courier/orders/${order.id}/status`, { to_status: to });
      onChanged();
    } catch (err) {
      /**
       * Перевод не подтверждён — самая частая осечка при выезде. Общее
       * «не удалось» здесь бесполезно: курьеру нужно понять, что делать,
       * а делать нужно не выезжать и ждать администратора.
       */
      if (err instanceof ApiError && err.code === ERROR_CODES.PAYMENT_NOT_CONFIRMED) {
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : t('actionFailed'));
      }
    } finally {
      setBusy(false);
    }
  }

  const address = [
    order.address_text,
    order.address_house && `${t('houseLabel')} ${order.address_house}`,
    order.address_entrance && `${t('entranceLabel')} ${order.address_entrance}`,
    order.address_floor && `${t('floorLabel')} ${order.address_floor}`,
    order.address_apartment && `${t('apartmentLabel')} ${order.address_apartment}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <main className="page">
      <h1 className="h1">
        {t('courierOrder')} #{order.public_number}
      </h1>

      <p style={{ margin: '0 0 var(--s-4)', color: statusColor(order.status) }}>
        {orderStatusLabel(order.status, user.locale)}
      </p>

      <section className="card">
        <div style={{ ...rowStyle, marginTop: 0 }}>
          <span className="muted">{t('slotTitle')}</span>
          <span>
            {order.slot_date}, {order.slot_start}–{order.slot_end}
          </span>
        </div>

        <div style={rowStyle}>
          <span className="muted">{t('clientLabel')}</span>
          <span>{order.contact_name}</span>
        </div>

        <div style={rowStyle}>
          <span className="muted">{t('toCollect')}</span>
          <strong>
            {formatMoney(order.total, user.locale)} {t('sum')}
          </strong>
        </div>

        <div style={rowStyle}>
          <span className="muted">{t('paymentTitle')}</span>
          <span>
            {order.payment_method === 'cash' ? t('paymentCash') : t('paymentCard')}
          </span>
        </div>
      </section>

      <section className="card" style={{ marginTop: 'var(--s-3)' }}>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          {t('addressLabel')}
        </p>
        <p style={{ margin: 'var(--s-1) 0 0' }}>{address}</p>
        {order.address_landmark && (
          <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
            {order.address_landmark}
          </p>
        )}

        <div style={{ display: 'grid', gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
          <a href={`tel:${order.contact_phone}`} style={secondaryButton}>
            {t('callClient')} · {order.contact_phone}
          </a>
          <a
            href={`https://www.openstreetmap.org/?mlat=${order.lat}&mlon=${order.lon}#map=18/${order.lat}/${order.lon}`}
            target="_blank"
            rel="noreferrer"
            style={secondaryButton}
          >
            {t('openMap')}
          </a>
        </div>
      </section>

      <section className="card" style={{ marginTop: 'var(--s-3)' }}>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
          {t('checkComposition')}
        </p>
        <ul style={{ margin: 'var(--s-2) 0 0', padding: 0, listStyle: 'none' }}>
          {order.items.map((i) => (
            <li key={i.id} style={{ ...rowStyle, marginTop: 'var(--s-1)' }}>
              <span>
                {i.name} × {i.qty}
              </span>
              <span className="muted">
                {formatMoney(i.line_total, user.locale)} {t('sum')}
              </span>
            </li>
          ))}
        </ul>
        {order.comment && (
          <p style={{ margin: 'var(--s-3) 0 0', fontSize: 'var(--fs-sm)' }}>{order.comment}</p>
        )}
      </section>

      {error && (
        <p
          role="alert"
          style={{ color: 'var(--c-danger)', fontSize: 'var(--fs-sm)', marginTop: 'var(--s-3)' }}
        >
          {error}
        </p>
      )}

      <div style={{ display: 'grid', gap: 'var(--s-2)', marginTop: 'var(--s-4)' }}>
        {order.status === 'new' &&
          (canConfirm ? (
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              data-loading={busy ? 'true' : undefined}
              onClick={() => void move('confirmed')}
            >
              {t('orderConfirm')}
            </button>
          ) : (
            // Модерация включена: заказ ждёт администратора, и объяснить это
            // лучше строкой, чем неработающей кнопкой
            <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
              {t('orderConfirmByAdmin')}
            </p>
          ))}

        {order.status === 'confirmed' && (
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            data-loading={busy ? 'true' : undefined}
            onClick={() => void move('assembled')}
          >
            {t('markAssembled')}
          </button>
        )}

        {order.status === 'assembled' && (
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            data-loading={busy ? 'true' : undefined}
            onClick={() => void move('on_the_way')}
          >
            {t('markOnTheWay')}
          </button>
        )}

        {order.status === 'on_the_way' && (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => {
                haptic('light');
                onDeliver();
              }}
            >
              {t('markDelivered')}
            </button>
            <button
              type="button"
              style={dangerButton}
              disabled={busy}
              onClick={() => {
                haptic('warning');
                onFail();
              }}
            >
              {t('markFailed')}
            </button>
          </>
        )}

        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('courierDeliveries')}
        </button>
      </div>
    </main>
  );
}
